/* eslint-disable prettier/prettier */
/* eslint-disable react/prop-types */
import ArticleIcon from '@mui/icons-material/Article'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import TagOutlinedIcon from '@mui/icons-material/TagOutlined'
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import SearchIcon from '@mui/icons-material/Search'
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    InputAdornment,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import moment from 'moment'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import Notice from '../../components/Notice'
import SingleSelect from '../../components/SingleSelect'
import {
    SUB_CATEGORIES,
    subCategoryLabel,
} from '../../helpers/templateCategories'
import TemplatePreview from '../../components/TemplatePreview'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import ListPagination from '../../components/shared/ListPagination'
import RowActions from '../../components/shared/RowActions'
import ErrorBanner from '../../components/shared/ErrorBanner'
import useCommsManager from '../../hooks/useCommsManager'
import { setAlert } from '../../features/appSlice'
import { clearTemplateData, putCommType } from '../../features/templateSlice'
import TemplateService from '../../services/templateService'
import { errorMessage } from '../../services/axios/errorMessage'
import { channelFrom } from '../../helpers/aiHandoff'
import {
    clientPage,
    pageFromParam,
    sizeFromParam,
} from '../../helpers/pagination'
import { AlertType } from '../../helpers/Constants'
import {
    downloadUnavailableReason,
    previewBody,
    saveTemplate,
} from '../../helpers/templateDownload'
import {
    SORT_OPTIONS,
    isPinpoint,
    metaStatusTag,
    sortTemplates,
} from '../../helpers/templateLibrary'
import {
    ACCENT,
    CATEGORY,
    CHANNEL_COLORS,
    GRADIENT,
    ICON,
    ON_ORANGE,
    RADIUS,
    SURFACE,
    TAG,
    TEXT,
    cardSx,
    pageSx,
    tint,
} from '../../theme/tokens'

const CHANNEL = {
    email: {
        icon: <EmailOutlinedIcon sx={{ fontSize: ICON.xs }} />,
        color: CHANNEL_COLORS.EMAIL,
        label: 'Email',
    },
    whatsapp: {
        icon: <WhatsAppIcon sx={{ fontSize: ICON.xs }} />,
        color: CHANNEL_COLORS.WHATSAPP,
        label: 'WhatsApp',
    },
    sms: {
        icon: <SmsOutlinedIcon sx={{ fontSize: ICON.xs }} />,
        color: CHANNEL_COLORS.SMS,
        label: 'SMS',
    },
    voice: {
        icon: <RecordVoiceOverOutlinedIcon sx={{ fontSize: ICON.xs }} />,
        color: CHANNEL_COLORS.VOICE,
        label: 'Voice',
    },
    push_notification: {
        icon: <NotificationsNoneOutlinedIcon sx={{ fontSize: ICON.xs }} />,
        color: CHANNEL_COLORS.PUSH_NOTIFICATION,
        label: 'Push',
    },
    slack: {
        icon: <TagOutlinedIcon sx={{ fontSize: ICON.xs }} />,
        color: CHANNEL_COLORS.SLACK,
        label: 'Slack',
    },
}

const CHANNEL_FILTERS = [
    { label: 'All', value: 'all', color: ACCENT.text },
    { label: 'EMAIL', value: 'email', color: CHANNEL_COLORS.EMAIL },
    { label: 'WHATSAPP', value: 'whatsapp', color: CHANNEL_COLORS.WHATSAPP },
    { label: 'SMS', value: 'sms', color: CHANNEL_COLORS.SMS },
    { label: 'VOICE', value: 'voice', color: CHANNEL_COLORS.VOICE },
    {
        label: 'PUSH',
        value: 'push_notification',
        color: CHANNEL_COLORS.PUSH_NOTIFICATION,
    },
    { label: 'SLACK', value: 'slack', color: CHANNEL_COLORS.SLACK },
]

// The WhatsApp sending accounts, as the old app names them.
const WA_CHANNEL_LABEL = {
    default: 'Engineering',
    'dce-12': 'Marketing',
    'people-ops': 'People Ops',
}

/**
 * The short facts a card shows under the author: for WhatsApp the category
 * and the sending account (the status WhatsApp gave it is a tag of its own,
 * metaStatusTag); for email a sender other than the default
 * (no-reply@bhanzu.com). The old library listed them as columns.
 */
export const templateFacts = (template) => {
    if (template?.comm_type === 'whatsapp')
        return [
            template.category &&
                template.category.charAt(0) +
                    template.category.slice(1).toLowerCase(),
            WA_CHANNEL_LABEL[template.channel || 'default'] ?? template.channel,
        ].filter(Boolean)
    // The sender's own default is not worth a line.
    if (
        template?.comm_type === 'email' &&
        template.source_email &&
        template.source_email !== 'no-reply@bhanzu.com'
    )
        return [`From ${template.source_email}`]
    return []
}

// A template's comms status as a TAG tone (theme/tokens.js; every pair is
// in the contrast test).
const TEMPLATE_STATUS = {
    approved: { tone: 'success', label: 'Approved' },
    approval_awaited: { tone: 'warning', label: 'Pending' },
    disapproved: { tone: 'error', label: 'Rejected' },
    active: { tone: 'success', label: 'Active' },
    inactive: { tone: 'neutral', label: 'Inactive' },
}

// The shape every tag on a card shares: the overline type on a small pill.
const tagSx = {
    display: 'inline-block',
    px: 0.75,
    py: 0.25,
    borderRadius: `${RADIUS.xs}px`,
    typography: 'overline',
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
}

const StatusTag = ({ status }) => {
    const s = TEMPLATE_STATUS[status] || { tone: 'neutral', label: status }
    const t = TAG[s.tone]
    return (
        <Box sx={{ ...tagSx, backgroundColor: t.bg, color: t.fg }}>
            {s.label}
        </Box>
    )
}

/** A tag in one of the TAG tones (theme/tokens.js; pairs in the contrast test). */
const ToneTag = ({ tone, children, title }) => {
    const t = TAG[tone] ?? TAG.neutral
    return (
        <Box
            title={title}
            data-tone={tone}
            sx={{ ...tagSx, backgroundColor: t.bg, color: t.fg }}
        >
            {children}
        </Box>
    )
}

// What a Pinpoint card's locked actions say.
export const PINPOINT_LOCKED = 'Managed in Amazon Pinpoint: read-only here'

const ChannelTag = ({ type }) => {
    const c = CHANNEL[type] || { color: TEXT.faint, icon: null, label: type }
    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.4,
                px: 0.75,
                py: 0.25,
                borderRadius: `${RADIUS.xs}px`,
                backgroundColor: tint(c.color, 0.12),
                color: c.color,
                typography: 'overline',
                lineHeight: 1.5,
            }}
        >
            {c.icon}
            {c.label || type?.toUpperCase() || '—'}
        </Box>
    )
}

// Prod holds ~3,400 templates. Rendering them all produced 84k DOM nodes and
// a 655MB heap, and every search keystroke re-rendered the lot (~600ms to
// paint). Render one page at a time instead: never more cards than the
// chosen page size.
export const TEMPLATE_PAGE_SIZES = [30, 60, 120]
export const DEFAULT_TEMPLATE_PAGE_SIZE = 60

const TemplateCard = React.memo(
    ({
        template,
        onPreview,
        onEdit,
        onTest,
        onDownload,
        onAction,
        manageNote,
    }) => {
        const facts = templateFacts(template)
        const pinpoint = isPinpoint(template)
        const metaStatus = metaStatusTag(template)
        const act = (action) => onAction(template, action)
        const [downloading, setDownloading] = useState(false)
        const cannotDownload = downloadUnavailableReason(template)
        const download = (event) => {
            event.stopPropagation()
            if (downloading) return
            setDownloading(true)
            onDownload(template).finally(() => setDownloading(false))
        }
        const onKeyDown = (event) => {
            // Only the card's own Enter/Space: one bubbling up from a focused
            // button inside it (Download, Test, Edit) is that button's activation,
            // which preventDefault here would cancel in favour of the preview.
            if (event.target !== event.currentTarget) return
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onPreview(template)
            }
        }
        return (
            <Paper
                elevation={0}
                role="button"
                tabIndex={0}
                aria-label={`Preview template ${template.template_name}`}
                onClick={() => onPreview(template)}
                onKeyDown={onKeyDown}
                sx={{
                    ...cardSx,
                    p: 2.5,
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, background 0.15s',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                        borderColor: SURFACE.borderHover,
                        background: SURFACE.cardHover,
                    },
                    '&:focus-visible': {
                        outline: 'none',
                        borderColor: ACCENT.border55,
                        background: ACCENT.bg08,
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1.5,
                    }}
                >
                    <ChannelTag type={template.comm_type} />
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 0.5,
                            flexWrap: 'wrap',
                            justifyContent: 'flex-end',
                        }}
                    >
                        {pinpoint ? (
                            <ToneTag tone="pinpoint" title={PINPOINT_LOCKED}>
                                Pinpoint
                            </ToneTag>
                        ) : (
                            <StatusTag status={template.status} />
                        )}
                    </Box>
                </Box>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        color: TEXT.primary,
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                    }}
                    title={template.template_name}
                >
                    {template.template_name}
                </Typography>
                <Typography
                    variant="caption"
                    component="p"
                    sx={{
                        color: TEXT.muted,
                        mb: 0.5,
                    }}
                >
                    {template.created_by}
                </Typography>
                <Typography
                    variant="caption"
                    component="p"
                    sx={{
                        color: TEXT.faint,
                        mb: facts.length || metaStatus ? 0.5 : 1.5,
                    }}
                    title="Category and sub-category"
                >
                    {template.sub_category
                        ? `${
                              subCategoryLabel(template.sub_category) ||
                              template.sub_category
                          }${
                              template.category
                                  ? ` · ${template.category.charAt(
                                        0
                                    )}${template.category
                                        .slice(1)
                                        .toLowerCase()}`
                                  : ''
                          }`
                        : 'Not tagged'}
                </Typography>
                {(facts.length > 0 || metaStatus) && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 0.75,
                            mb: 1.5,
                        }}
                    >
                        {/* A template WhatsApp does not approve does not send,
                        whatever its comms status says. Here, not beside the
                        status tag, so every card's name stays on one line. */}
                        {metaStatus && (
                            <ToneTag tone={metaStatus.tone}>
                                {metaStatus.label}
                            </ToneTag>
                        )}
                        {facts.length > 0 && (
                            <Typography
                                variant="caption"
                                sx={{ color: TEXT.faint }}
                            >
                                {facts.join(' · ')}
                            </Typography>
                        )}
                    </Box>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pt: 1.5,
                        borderTop: `1px solid ${SURFACE.border}`,
                        mt: 'auto',
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: TEXT.faint,
                            whiteSpace: 'nowrap',
                            mr: 1,
                        }}
                    >
                        {typeof template.created_on === 'number'
                            ? moment(template.created_on * 1000).format(
                                  'DD MMM YYYY'
                              )
                            : ''}
                    </Typography>
                    <RowActions
                        name={template.template_name}
                        stopPropagation
                        actions={[
                            {
                                key: 'preview',
                                title: 'Preview',
                                label: `Preview ${template.template_name}`,
                                icon: <VisibilityOutlinedIcon />,
                                onClick: () => onPreview(template),
                                hoverColor: CATEGORY.blue,
                            },
                            {
                                key: 'edit',
                                title: 'Edit',
                                tooltip: pinpoint
                                    ? PINPOINT_LOCKED
                                    : manageNote
                                    ? `Edit: ${manageNote}`
                                    : template.comm_type === 'whatsapp'
                                    ? 'Edit: a WhatsApp template is duplicated to change it'
                                    : 'Edit',
                                label: `Edit ${template.template_name}`,
                                icon: <EditOutlinedIcon />,
                                disabled: pinpoint || Boolean(manageNote),
                                onClick: () => onEdit(template),
                            },
                            {
                                key: 'test',
                                title: 'Send a test',
                                note: pinpoint ? PINPOINT_LOCKED : undefined,
                                icon: <ScienceOutlinedIcon />,
                                disabled: pinpoint,
                                onClick: () => onTest(template),
                            },
                            {
                                key: 'download',
                                title: downloading ? 'Downloading' : 'Download',
                                note: cannotDownload || undefined,
                                icon: downloading ? (
                                    <CircularProgress
                                        size={16}
                                        aria-label="Downloading"
                                        sx={{ color: TEXT.secondary }}
                                    />
                                ) : (
                                    <FileDownloadOutlinedIcon />
                                ),
                                disabled: !!cannotDownload || downloading,
                                onClick: download,
                            },
                            // As in the old app: an email's status is set by
                            // hand; other channels' come from approval. A
                            // Pinpoint template has no status or delete here.
                            !pinpoint &&
                                template.comm_type === 'email' && {
                                    key: 'status',
                                    title:
                                        template.status === 'approved'
                                            ? 'Mark inactive'
                                            : 'Mark approved',
                                    note: manageNote || undefined,
                                    icon: <ToggleOnOutlinedIcon />,
                                    disabled: Boolean(manageNote),
                                    onClick: () =>
                                        act(
                                            template.status === 'approved'
                                                ? 'inactive'
                                                : 'approved'
                                        ),
                                },
                            !pinpoint && {
                                key: 'delete',
                                title: 'Delete',
                                note: manageNote || undefined,
                                icon: <DeleteOutlineIcon />,
                                disabled: Boolean(manageNote),
                                onClick: () => act('delete'),
                                destructive: true,
                            },
                        ]}
                    />
                </Box>
            </Paper>
        )
    }
)
TemplateCard.displayName = 'TemplateCard'

const EmptyTemplates = () => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 10,
            color: TEXT.faint,
        }}
    >
        <ArticleIcon
            sx={{ fontSize: ICON.hero, mb: 2, color: TEXT.decorative }}
        />
        <Typography
            variant="h6"
            component="p"
            sx={{
                color: TEXT.muted,
                mb: 0.5,
            }}
        >
            No templates found
        </Typography>
        <Typography variant="caption" sx={{ color: TEXT.faint }}>
            Adjust the filters or create a new template to get started.
        </Typography>
    </Box>
)

const Templates = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const location = useLocation()
    // Why create, upload, edit, status and delete are off for this user, or
    // '' when they may (the comms manager role, as in the old app).
    const { note: manageNote } = useCommsManager()
    // The card action waiting for confirmation: {template, action}.
    const [pendingAction, setPendingAction] = useState(null)
    const { templates, pinpointTemplates, pinpointError } = useSelector(
        (state) => state.template
    )
    // The CommTemplates rows and Pinpoint's read-only ones, one library.
    const allTemplates = useMemo(
        () => [...templates, ...(pinpointTemplates ?? [])],
        [templates, pinpointTemplates]
    )
    // Campaign Brief's Browse Templates sends its recommended channel.
    const [subCategoryFilter, setSubCategoryFilter] = useState('all')
    const [channelFilter, setChannelFilter] = useState(() =>
        channelFrom(
            location.state?.channel,
            CHANNEL_FILTERS.map((f) => f.value),
            'all'
        )
    )
    const [search, setSearch] = useState('')
    const [sortOrder, setSortOrder] = useState('newest')
    // 'loading' | 'ready' | 'error'. Without it a slow or failed fetch read
    // as "0 templates available" and "No templates found".
    const [loadState, setLoadState] = useState('loading')

    // Pinpoint is asked alongside; its failure leaves the rest of the
    // library as it is and shows a notice.
    const loadTemplates = () => {
        setLoadState('loading')
        dispatch(TemplateService.getPinpointTemplates('sms'))
        dispatch(TemplateService.getTemplates({ background: true })).then(
            (resp) => setLoadState(resp.error ? 'error' : 'ready')
        )
    }

    useEffect(() => {
        // A preview (or its error) left open by another page must not pop up here.
        dispatch(clearTemplateData())
        loadTemplates()
    }, [])
    // A list already in the store (from an earlier visit) stays on screen
    // while it refreshes; the loading and error states replace it only when
    // there is nothing to show.
    const hasList = templates.length > 0

    const onPreview = (template) => {
        if (isPinpoint(template)) {
            dispatch(putCommType('sms'))
            dispatch(
                TemplateService.getPinpointTemplate({
                    templateName: template.template_name,
                    preview: true,
                })
            )
            return
        }
        dispatch(putCommType(template.comm_type))
        dispatch(TemplateService.getTemplateData(template.id))
        // The header, footer and buttons (and the header media's link) are
        // on the template's row, not in its preview body.
        if (template.comm_type === 'whatsapp')
            dispatch(TemplateService.getTemplateDetails(template.id))
    }

    // Comms Finder's Open template sends the template's id: once the list is
    // in, narrow it to that template and open its preview. An id the list
    // does not have still opens the preview, which says why it cannot show.
    const askedFor = location.state?.templateId
    const openedAskedFor = useRef(false)
    useEffect(() => {
        if (!askedFor || openedAskedFor.current || loadState !== 'ready') return
        openedAskedFor.current = true
        const template = templates.find((t) => t.id === askedFor)
        if (template) {
            setSearch(template.template_name ?? '')
            onPreview(template)
        } else {
            dispatch(TemplateService.getTemplateData(askedFor))
        }
    }, [askedFor, loadState, templates])

    // Names are user-typed ("Referral Template test",
    // "...mailyourchild'sstudentdashboardisnowlive!"); encode them so a space,
    // "&", "#" or "/" cannot break or truncate the URL.
    const onEdit = (template) =>
        navigate(
            `/edit-template?template_id=${encodeURIComponent(
                template.id
            )}&template_name=${encodeURIComponent(template.template_name)}`
        )

    const onTest = (template) =>
        navigate(
            `/test-template/${encodeURIComponent(
                template.id
            )}/${encodeURIComponent(template.template_name)}/${
                template.comm_type
            }`
        )

    // Fetches the body without the preview's thunk (whose reducer opens the
    // dialog) and saves it exactly as stored, placeholders included. Resolves
    // either way, so the card can stop its spinner.
    const onDownload = (template) =>
        dispatch(
            isPinpoint(template)
                ? TemplateService.getPinpointTemplate({
                      templateName: template.template_name,
                  })
                : TemplateService.getTemplateBody(template.id)
        ).then((resp) => {
            const name = template.template_name || template.id
            const response = resp.error ? null : resp.payload?.data?.response
            const body = isPinpoint(template)
                ? String(response?.Body ?? '')
                : previewBody(response)
            if (body) {
                saveTemplate(body, { name, commType: template.comm_type })
                return
            }
            dispatch(
                setAlert({
                    open: true,
                    type: AlertType.error,
                    message: resp.error
                        ? `${name} could not be downloaded: ${errorMessage({
                              data: resp.payload,
                          })}`
                        : `${name} has an empty body: there is nothing to download.`,
                })
            )
        })

    const onAction = (template, action) =>
        setPendingAction({ template, action })

    /** Run the confirmed action, then reload the library so the card shows it. */
    const confirmAction = () => {
        const { template, action } = pendingAction
        setPendingAction(null)
        const name = template.template_name || template.id
        const request =
            action === 'delete'
                ? TemplateService.deleteTemplate(template.id)
                : TemplateService.approveTemplate({
                      templateID: template.id,
                      status: action,
                  })
        dispatch(request).then((resp) => {
            // A status change answers with its own message; a delete does not.
            if (action === 'delete')
                dispatch(
                    setAlert({
                        open: true,
                        type: resp.error ? AlertType.error : AlertType.success,
                        message: resp.error
                            ? `${name} was not deleted: ${errorMessage({
                                  data: resp.payload,
                              })}`
                            : `${name} is deleted.`,
                    })
                )
            if (!resp.error) loadTemplates()
        })
    }

    // Debounce the search term: filtering 3,400 rows on every keystroke was
    // the dominant cost, not the filter itself.
    const [debouncedSearch, setDebouncedSearch] = useState('')
    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(search), 180)
        return () => clearTimeout(id)
    }, [search])

    const filtered = useMemo(() => {
        const needle = debouncedSearch.trim().toLowerCase()
        return sortTemplates(
            allTemplates
                .filter(
                    (t) =>
                        channelFilter === 'all' || t.comm_type === channelFilter
                )
                .filter(
                    (t) =>
                        subCategoryFilter === 'all' ||
                        (subCategoryFilter === 'untagged'
                            ? !t.sub_category
                            : t.sub_category === subCategoryFilter)
                )
                .filter(
                    (t) =>
                        !needle ||
                        t.template_name?.toLowerCase().includes(needle)
                ),
            sortOrder
        )
    }, [
        allTemplates,
        channelFilter,
        subCategoryFilter,
        debouncedSearch,
        sortOrder,
    ])

    // The page and its size live in the URL (?page=3&size=60, the page
    // counted from 1), so a reload or Back returns to the same place. A page
    // that is not a whole number is the first; one past the end shows the
    // last.
    const [searchParams, setSearchParams] = useSearchParams()
    const requestedPage = pageFromParam(searchParams.get('page'))
    const pageSize = sizeFromParam(
        searchParams.get('size'),
        TEMPLATE_PAGE_SIZES,
        DEFAULT_TEMPLATE_PAGE_SIZE
    )
    const view = clientPage({ rows: filtered, page: requestedPage, pageSize })
    const visible = view.rows

    /**
     * Put a page (from 0) and size in the URL, keeping its other parameters
     * and the router state (a channel or template handed over by another
     * page). `replace` for corrections and resets, so Back skips them.
     */
    const writePaging = (page, size, replace = false) => {
        const next = new URLSearchParams(searchParams)
        next.set('page', String(page + 1))
        next.set('size', String(size))
        setSearchParams(next, { replace, state: location.state })
    }

    // One place decides what the URL should say, since two writes in one
    // render would both start from the same old URL and the last would win:
    // - a new result set (channel, sub-category, search or sort) starts at
    //   page 1, so a narrowed search does not leave you on a page that no
    //   longer exists (not on the first render: the URL's page stands then);
    // - once the library is in, a page past the end (or a page or size that
    //   is not one) is corrected to what is shown.
    const filterKey = JSON.stringify([
        channelFilter,
        subCategoryFilter,
        debouncedSearch,
        sortOrder,
    ])
    const lastFilterKey = useRef(filterKey)
    const pageParam = searchParams.get('page')
    const sizeParam = searchParams.get('size')
    useEffect(() => {
        const filtersChanged = lastFilterKey.current !== filterKey
        lastFilterKey.current = filterKey
        if (filtersChanged) {
            if (requestedPage !== 0) writePaging(0, pageSize, true)
            return
        }
        if (loadState !== 'ready') return
        const pageWrong =
            pageParam !== null && pageParam !== String(view.page + 1)
        const sizeWrong = sizeParam !== null && sizeParam !== String(pageSize)
        if (pageWrong || sizeWrong) writePaging(view.page, pageSize, true)
    }, [filterKey, loadState, pageParam, sizeParam, view.page, pageSize])

    // The top of the card grid, scrolled into view on a page change.
    const gridTopRef = useRef(null)
    const onPageChange = (page) => {
        writePaging(page, pageSize)
        gridTopRef.current?.scrollIntoView?.({
            behavior: 'smooth',
            block: 'start',
        })
    }
    const onPageSizeChange = (size) => writePaging(0, size)

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 3, md: 5 } }}>
                {/* Page Header: the actions drop under the title on a
                    phone instead of pushing the page wider. */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        mb: 4,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: `${RADIUS.md}px`,
                                background: GRADIENT.brand,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            {/* Deep Blue on the orange-to-yellow tile in
                                both themes (white is 3.3:1 on orange). */}
                            <ArticleIcon
                                sx={{ fontSize: ICON.xl, color: ON_ORANGE }}
                            />
                        </Box>
                        <Box>
                            <Typography
                                variant="h2"
                                component="h1"
                                sx={{ color: TEXT.primary, lineHeight: 1.2 }}
                            >
                                Template Library
                            </Typography>
                            <Typography
                                variant="caption"
                                component="p"
                                sx={{
                                    color: TEXT.muted,
                                    mt: 0.25,
                                }}
                            >
                                {!hasList && loadState === 'loading'
                                    ? 'Loading templates…'
                                    : !hasList && loadState === 'error'
                                    ? 'Templates could not be loaded'
                                    : `${allTemplates.length} template${
                                          allTemplates.length !== 1 ? 's' : ''
                                      } available`}
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}
                    >
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={loadTemplates}
                            disabled={loadState === 'loading'}
                        >
                            Refresh
                        </Button>
                        <Tooltip title={manageNote}>
                            <span>
                                <Button
                                    variant="contained"
                                    size="small"
                                    disabled={Boolean(manageNote)}
                                    onClick={() => navigate('/create-template')}
                                    // The theme's contained button: Deep
                                    // Blue on orange (white was 3.3:1), a
                                    // pill, and its disabled look.
                                    sx={{ fontWeight: 600, px: 2 }}
                                >
                                    Create Template
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Filters */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        mb: 3,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {CHANNEL_FILTERS.map((f) => {
                            const active = channelFilter === f.value
                            return (
                                <Chip
                                    key={f.value}
                                    label={f.label}
                                    onClick={() => setChannelFilter(f.value)}
                                    size="small"
                                    sx={{
                                        typography: 'caption',
                                        fontWeight: 600,
                                        height: 28,
                                        borderRadius: `${RADIUS.pill}px`,
                                        cursor: 'pointer',
                                        border: `1px solid ${
                                            active ? f.color : SURFACE.border
                                        }`,
                                        background: active
                                            ? tint(f.color, 0.12)
                                            : SURFACE.inputBg,
                                        color: active ? f.color : TEXT.muted,
                                        // An active chip keeps its 12% tint on
                                        // hover: a stronger tint drops the
                                        // amber and pink labels under AA in
                                        // the light theme.
                                        '&:hover': {
                                            background: active
                                                ? tint(f.color, 0.12)
                                                : SURFACE.hover,
                                            color: active
                                                ? f.color
                                                : TEXT.secondary,
                                        },
                                        '& .MuiChip-label': { px: 1.25 },
                                    }}
                                />
                            )
                        })}
                    </Box>
                    <Box sx={{ minWidth: 240 }}>
                        <SingleSelect
                            label="Sub-category"
                            options={[
                                { label: 'All sub-categories', value: 'all' },
                                ...SUB_CATEGORIES,
                                { label: 'Not tagged', value: 'untagged' },
                            ]}
                            optionLabelText="label"
                            optionValueText="value"
                            value={subCategoryFilter}
                            handleChange={setSubCategoryFilter}
                        />
                    </Box>
                    <TextField
                        size="small"
                        placeholder="Search templates…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        sx={{
                                            fontSize: ICON.md,
                                            color: TEXT.muted,
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ ml: 'auto', minWidth: 240 }}
                    />
                    <Box sx={{ width: 170 }}>
                        <SingleSelect
                            label="Sort by"
                            options={SORT_OPTIONS}
                            optionLabelText="label"
                            optionValueText="value"
                            value={sortOrder}
                            handleChange={setSortOrder}
                        />
                    </Box>
                </Box>

                {pinpointError && (
                    <Box sx={{ mb: 3 }}>
                        <Notice
                            notices={[
                                `The Amazon Pinpoint SMS templates could not be loaded (${pinpointError}), so they are missing from this list. Refresh to try again.`,
                            ]}
                        />
                    </Box>
                )}

                {/* Card grid */}
                <Box ref={gridTopRef} sx={{ scrollMarginTop: 64 }} />
                {!hasList && loadState === 'loading' ? (
                    <Box
                        role="status"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5,
                            py: 10,
                        }}
                    >
                        <CircularProgress
                            size={20}
                            sx={{ color: TEXT.muted }}
                        />
                        <Typography variant="body2" sx={{ color: TEXT.muted }}>
                            Loading templates…
                        </Typography>
                    </Box>
                ) : !hasList && loadState === 'error' ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 2,
                            py: 4,
                        }}
                    >
                        <ErrorBanner message="The template library could not be loaded. Check your connection and try again." />
                        <Button variant="outlined" onClick={loadTemplates}>
                            Try again
                        </Button>
                    </Box>
                ) : filtered.length === 0 ? (
                    <EmptyTemplates />
                ) : (
                    <Grid container spacing={2}>
                        {visible.map((t) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={t.id}>
                                <TemplateCard
                                    template={t}
                                    onPreview={onPreview}
                                    onEdit={onEdit}
                                    onTest={onTest}
                                    onDownload={onDownload}
                                    onAction={onAction}
                                    manageNote={manageNote}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
                {/* With the cards: not while the first load or its error
                    stands in for them. */}
                {(hasList || loadState === 'ready') && view.total > 0 && (
                    <Box sx={{ mt: 4 }}>
                        <ListPagination
                            label="Templates pages"
                            sizeLabel="Cards per page"
                            page={view.page}
                            pageSize={pageSize}
                            pageSizeOptions={TEMPLATE_PAGE_SIZES}
                            onPageChange={onPageChange}
                            onPageSizeChange={onPageSizeChange}
                            from={view.from}
                            to={view.to}
                            total={view.total}
                            hasNext={view.hasNext}
                            pageCount={view.pageCount}
                        />
                    </Box>
                )}
            </Box>
            <TemplatePreview />
            <ConfirmDialog
                open={Boolean(pendingAction)}
                title={
                    pendingAction?.action === 'delete'
                        ? 'Delete this template?'
                        : 'Change its status?'
                }
                message={
                    !pendingAction
                        ? ''
                        : pendingAction.action === 'delete'
                        ? `${
                              pendingAction.template.template_name
                          } stops sending and leaves the library${
                              pendingAction.template.comm_type === 'whatsapp'
                                  ? ', and it is deleted in WhatsApp too'
                                  : ''
                          }. Pipelines that use it send nothing on this channel.`
                        : `${pendingAction.template.template_name} goes from ${pendingAction.template.status} to ${pendingAction.action}. Only an approved template sends.`
                }
                confirmLabel={
                    pendingAction?.action === 'delete'
                        ? 'Delete'
                        : pendingAction?.action === 'approved'
                        ? 'Mark approved'
                        : 'Mark inactive'
                }
                destructive={pendingAction?.action !== 'approved'}
                onConfirm={confirmAction}
                onCancel={() => setPendingAction(null)}
            />
        </Box>
    )
}

export default Templates
